<template>
    <div class="page">
        <reservation-header :status="form.id ? 'Saved' : 'New'" :title="formTitle" @save="onSave" @delete="onDelete" />

        <div class="ltr-form-container">
            <div class="form-header">
                <!-- Basic Information Section -->
                <div class="basic-info-section">
                    <h2 class="section-title">Osnovni Podaci<span v-if="form.vehicle_type_selection"
                            class="section-title-type"> — {{ vehicleTypeLabel }}</span></h2>

                    <div class="form-grid">
                        <!-- Row 1 - Title and Status -->
                        <div class="form-group">
                            <label class="form-label">Naziv kalkulacije</label>
                            <input type="text" class="form-control" v-model="form.title"
                                placeholder="Unesite naziv kalkulacije" />
                        </div>

                        <div class="form-group">
                            <label class="form-label">Status</label>
                            <select class="form-control" v-model="form.status">
                                <option value="Draft">Draft</option>
                                <option value="Final">Final</option>
                            </select>
                        </div>

                        <div class="form-group">
                            <label class="form-label">Datum</label>
                            <input type="date" class="form-control" v-model="form.date" @change="onFieldChange" />
                        </div>

                        <div class="form-group">
                            <label class="form-label">Kupac</label>
                            <input type="text" class="form-control" v-model="form.customer" />
                        </div>

                        <!-- Row 2 -->
                        <div class="form-group">
                            <label class="form-label">Tip Vozila</label>
                            <select class="form-control" v-model="form.vehicle_type" @change="onVehicleTypeChange">
                                <option value="Obicno">Obicno</option>
                                <option value="m-HEV">m-HEV</option>
                                <option value="Teretno">Teretno</option>
                                <option value="Elektricno">Električno</option>
                            </select>
                        </div>

                        <div class="form-group">
                            <label class="form-label">Broj sedišta</label>
                            <select class="form-control" v-model="form.seats" @change="onSeatsChange">
                                <option value="1+3">1+3</option>
                                <option value="1+4">1+4</option>
                                <option value="1+5+">1+5+</option>
                            </select>
                        </div>

                        <div class="form-group">
                            <label class="form-label">Proizvođač</label>
                            <input type="text" class="form-control" v-model="form.manufacturer" />
                        </div>

                        <div class="form-group">
                            <label class="form-label">Vozilo</label>
                            <input type="text" class="form-control" v-model="form.vehicle" />
                        </div>

                        <!-- Row 3 -->
                        <div class="form-group">
                            <label class="form-label">Model (oznaka motora i oprema)</label>
                            <input type="text" class="form-control" v-model="form.vehicle_model" />
                        </div>

                        <div class="form-group">
                            <label class="form-label">CCM</label>
                            <select class="form-control" v-model="form.ccm" @change="onCcmChange">
                                <option value="">-- Izaberite zapreminu --</option>
                                <option v-for="ccmOption in ccmOptions" :key="ccmOption" :value="ccmOption">
                                    {{ ccmOption }}
                                </option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label class="form-label">KW</label>
                            <input v-if="form.vehicle_type === 'Elektricno'" type="text" class="form-control"
                                v-model="form.kw" disabled />
                            <select v-else class="form-control" v-model="form.kw" @change="onKwChange">
                                <option value="">-- Izaberite snagu --</option>
                                <option v-for="kwOption in kwOptions" :key="kwOption" :value="kwOption">
                                    {{ kwOption }}
                                </option>
                            </select>
                        </div>



                        <div class="form-group" v-if="form.vehicle_type_selection === 'used_primero'">
                            <label class="form-label">Nab. vr. pri ulasku u flotu BRUTO</label>
                            <input type="number" step="0.01" class="form-control"
                                v-model.number="form.new_vehicle_value_gross" @blur="calculateKasko" />
                            <small class="text-muted">Osnovica za KASKO</small>
                        </div>

                        <div class="form-group" v-if="form.vehicle_type_selection === 'used_other'">
                            <label class="form-label">Godišnje Kasko Osiguranje NETO</label>
                            <input type="number" step="0.01" class="form-control" v-model.number="form.annual_kasko_net"
                                @blur="calculateTotals" />
                        </div>

                        <div class="form-group">
                            <label class="form-label">Prodajna cena sa PDV-om</label>
                            <input type="number" step="0.01" class="form-control"
                                v-model.number="form.vehicle_value_list_pdv_discounted" @blur="calculateValues" />
                        </div> <!-- Row 4 - Calculated values -->
                        <div class="form-group">
                            <label class="form-label">Neto vrednost vozila</label>
                            <input type="number" step="0.01" class="form-control"
                                v-model.number="form.vehicle_value_net" readonly />
                        </div>

                        <div class="form-group">
                            <label class="form-label">PDV na vrednost vozila</label>
                            <input type="number" step="0.01" class="form-control"
                                v-model.number="form.vehicle_value_vat" readonly />
                        </div>

                        <div class="form-group">
                            <label class="form-label">Period trajanja ugovora (meseci)</label>
                            <select class="form-control" v-model.number="form.contract_duration"
                                @change="onContractDurationChange">
                                <option :value="12">12</option>
                                <option :value="24">24</option>
                                <option :value="36">36</option>
                                <option :value="48">48</option>
                                <option :value="60">60</option>
                            </select>
                        </div>

                        <div class="form-group">
                            <label class="form-label">Godišnja kilometraža</label>
                            <input type="number" class="form-control" v-model.number="form.annual_mileage" />
                        </div>

                        <div class="form-group">
                            <label class="form-label">Ostatak vrednosti sa PDV-om (%)</label>
                            <input type="number" step="0.01" class="form-control"
                                v-model.number="form.vehicle_value_remaining" placeholder="30"
                                @blur="calculateResidualValue" />
                        </div>

                        <!-- Row 5 -->
                        <div class="form-group">
                            <label class="form-label">Ostatak vrednosti sa PDV-om (EUR)</label>
                            <input type="number" step="0.01" class="form-control"
                                v-model.number="form.vehicle_value_remaining_amount"
                                @blur="calculateResidualPercentage" />
                        </div>
                        <!-- Hidden: Uvećana otkupna vrednost fields - not used in calculations
                        <div class="form-group">
                            <label class="form-label">Uvećana otkupna vrednost (%)</label>
                            <input type="number" step="0.01" class="form-control"
                                v-model.number="form.increased_buyout_percentage" placeholder="5"
                                @blur="calculateIncreasedBuyout" />
                        </div>

                        <div class="form-group">
                            <label class="form-label">Uvećana otkupna vrednost (EUR)</label>
                            <input type="number" step="0.01" class="form-control"
                                v-model.number="form.vehicle_value_increased" readonly />
                        </div>
                        -->

                        <div class="form-group" v-if="form.vehicle_type_selection === 'used_primero'">
                            <label class="form-label">Broj registracije</label>
                            <input type="text" class="form-control" v-model="form.registration_number" />
                        </div>

                        <div class="form-group form-group-full">
                            <label class="form-label">Komentar</label>
                            <textarea class="form-control" rows="3" v-model="form.comment"></textarea>
                        </div>
                    </div>
                </div>

                <!-- Tabs Navigation -->
                <div class="mt-4">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                        <h5 class="mb-0">Kalkulacije i objašnjenja</h5>
                        <div>
                            <button type="button" class="btn btn-success btn-sm bi bi-plus" @click="addCalculation">
                                Dodaj kalkulaciju</button>
                        </div>
                    </div>
                    <ul class="nav nav-tabs" role="tablist">
                        <li class="nav-item" role="presentation" v-for="(calc, idx) in form.calculations" :key="idx">
                            <button class="nav-link" :class="{ active: activeCalc === idx }" @click="activeCalc = idx"
                                type="button" role="tab">
                                {{ calc.calc_title || `Kalkulacija ${idx + 1}` }}
                            </button>
                        </li>
                    </ul>
                    <div class="tab-content border border-top-0 p-3">
                        <div class="tab-pane fade show active" role="tabpanel" v-if="currentCalc">
                            <div class="d-flex justify-content-end mb-2">
                                <button type="button" class="btn btn-outline-danger btn-sm bi bi-trash"
                                    @click="removeCalculation(activeCalc)"> Ukloni</button>
                            </div>

                            <!-- Totals/Proracun Section -->
                            <div class="form-section total-section">
                                <h3 class="section-header">Proračun</h3>

                                <!-- Summary Info from Basic Data -->
                                <div class="proracun-summary">
                                    <div class="summary-row">
                                        <div class="summary-item-inline">
                                            <span class="summary-info-label">Vrednost sa PDV:</span>
                                            <span class="summary-info-value">{{
                                                formatCurrency(form.vehicle_value_list_pdv_discounted) }}</span>
                                        </div>
                                        <div class="summary-item-inline">
                                            <span class="summary-info-label">Vrednost bez PDV:</span>
                                            <span class="summary-info-value">{{ formatCurrency(form.vehicle_value_net)
                                            }}</span>
                                        </div>
                                        <div class="summary-item-inline">
                                            <span class="summary-info-label">PDV:</span>
                                            <span class="summary-info-value">{{ formatCurrency(form.vehicle_value_vat)
                                            }}</span>
                                        </div>
                                        <div class="summary-item-inline">
                                            <span class="summary-info-label">Period:</span>
                                            <span class="summary-info-value">{{ form.contract_duration }} mes.</span>
                                        </div>
                                    </div>
                                    <div class="summary-row">
                                        <div class="summary-item-inline">
                                            <span class="summary-info-label">Ostatak vrednosti (%):</span>
                                            <span class="summary-info-value">{{ form.vehicle_value_remaining }}%</span>
                                        </div>
                                        <div class="summary-item-inline">
                                            <span class="summary-info-label">Ostatak vrednosti (EUR):</span>
                                            <span class="summary-info-value">{{
                                                formatCurrency(form.vehicle_value_remaining_amount) }}</span>
                                        </div>
                                        <div class="summary-item-inline">
                                            <span class="summary-info-label">Iznos finansiranja:</span>
                                            <span class="summary-info-value">{{
                                                formatCurrency(currentCalc?.financing_amount) }}</span>
                                        </div>
                                        <div class="summary-item-inline">
                                            <!-- Empty space for alignment -->
                                        </div>
                                    </div>
                                </div>

                                <div class="totals-grid">
                                    <!-- Row 1 -->
                                    <div class="total-item">
                                        <label class="total-label">Učešće od neto cene (%)</label>
                                        <input type="number" step="0.01" class="form-control"
                                            v-model.number="currentCalc.participation_percentage"
                                            @blur="calculateProracun" placeholder="0" />
                                    </div>

                                    <div class="total-item">
                                        <label class="total-label">Učešće od neto cene (EUR)</label>
                                        <input type="number" step="0.01" class="form-control"
                                            v-model.number="currentCalc.participation_amount" readonly />
                                    </div>

                                    <div class="total-item">
                                        <label class="total-label">Reprogramiranje</label>
                                        <select class="form-control" v-model="currentCalc.reprogramming_note">
                                            <option value="">-- Izaberite --</option>
                                            <option value="Ne reprogramira se">Ne reprogramira se</option>
                                            <option value="6 Mesecni euribor">6 Mesecni euribor</option>
                                            <option value="3 Mesecni euribor">3 Mesecni euribor</option>
                                        </select>
                                    </div>

                                    <div class="total-item">
                                        <label class="total-label">Kamatna stopa (%)</label>
                                        <input type="number" step="0.01" class="form-control"
                                            v-model.number="currentCalc.interest_rate" @blur="calculateProracun"
                                            placeholder="0" />
                                    </div>

                                    <!-- Row 2 -->
                                    <div class="total-item">
                                        <label class="total-label">Kamatna stopa finan. PDV (%)</label>
                                        <input type="number" step="0.01" class="form-control"
                                            v-model.number="currentCalc.vat_financing_rate" @blur="calculateProracun"
                                            placeholder="0" />
                                    </div>

                                    <div class="total-item">
                                        <label class="total-label">Troškovi obrade sa PDV (%)</label>
                                        <input type="number" step="0.01" class="form-control"
                                            v-model.number="currentCalc.processing_fee_percentage"
                                            @blur="calculateProracun" placeholder="0" />
                                    </div>

                                    <div class="total-item">
                                        <label class="total-label">Troškovi obrade sa PDV (EUR)</label>
                                        <input type="number" step="0.01" class="form-control"
                                            v-model.number="currentCalc.processing_fee_amount" readonly />
                                    </div>

                                    <!-- Row 3 -->
                                    <div class="total-item">
                                        <label class="total-label">Mesečna rata Neto</label>
                                        <input type="number" step="0.01" class="form-control"
                                            v-model.number="currentCalc.monthly_installment_net" readonly />
                                    </div>

                                    <div class="total-item">
                                        <label class="total-label">Finanisiran PDV mesečno</label>
                                        <input type="number" step="0.01" class="form-control"
                                            v-model.number="currentCalc.financed_pdv_monthly" readonly />
                                    </div>
                                </div>
                            </div>

                            <!-- Manipulative Costs Section -->
                            <div class="form-section">
                                <h3 class="section-header">Manipulativni Troškovi</h3>

                                <div class="costs-grid">
                                    <div class="cost-item">
                                        <label class="cost-label">Auto kasko (Ukupno)</label>
                                        <input type="number" step="0.01" class="form-control"
                                            v-model.number="currentCalc.auto_kasko_total" readonly />
                                        <small class="form-text text-muted">Iz šifarnika na osnovu neto
                                            vrednosti</small>
                                    </div>

                                    <div class="cost-item">
                                        <label class="cost-label">REGISTRACIJA VOZILA (Ukupno)</label>
                                        <input type="number" step="0.01" class="form-control"
                                            v-model.number="currentCalc.registration_total" readonly />
                                        <small class="form-text text-muted">Iz šifarnika na osnovu tipa i ccm
                                            opsega</small>
                                    </div>

                                    <div class="cost-item">
                                        <label class="cost-label">OBAVEZNA OPREMA (Ukupno EUR)</label>
                                        <input type="number" step="0.01" class="form-control"
                                            v-model.number="currentCalc.mandatory_equipment_total"
                                            @blur="calculateTotals" placeholder="100" />
                                    </div>

                                    <div class="cost-item">
                                        <label class="cost-label">AO i lično osiguranje (Ukupno)</label>
                                        <input type="number" step="0.01" class="form-control"
                                            v-model.number="currentCalc.ao_personal_insurance_total" readonly />
                                        <small class="form-text text-muted">Iz šifarnika na osnovu broja sedišta i
                                            kw</small>
                                    </div>

                                    <div class="cost-item">
                                        <label class="cost-label">Manipulativni troškovi</label>
                                        <input type="number" step="0.01" class="form-control"
                                            v-model.number="currentCalc.handling_fee_basic" readonly />
                                        <small class="form-text text-muted">Automatski: neto vrednost vozila ×
                                            {{ currentCalc.handling_fee_percentage ||
                                                defaults.manipulativni_troskovi_procenat || 0.15 }}%</small>
                                    </div>

                                    <div class="cost-item">
                                        <label class="cost-label">Mehanička zaštita - igla (Ukupno EUR)</label>
                                        <input type="number" step="0.01" class="form-control"
                                            v-model.number="currentCalc.mechanical_protection_total"
                                            @blur="checkMechanicalProtection" />
                                        <small class="form-text text-muted" v-if="form.vehicle_value_net >= 35000">
                                            ⚠️ Obavezna za vozila vrednosti 35.000€ i više (preporučeno: 300€ + PDV)
                                        </small>
                                    </div>

                                    <div class="cost-item">
                                        <label class="cost-label">Marža (%)</label>
                                        <input type="number" step="0.01" class="form-control"
                                            v-model.number="currentCalc.margin_percentage" @blur="calculateTotals"
                                            placeholder="9" />
                                    </div>

                                    <div class="cost-item">
                                        <label class="cost-label">Europcar fee (%)</label>
                                        <input type="number" step="0.01" class="form-control"
                                            v-model.number="currentCalc.europcar_fee_percentage" @blur="calculateTotals"
                                            placeholder="1.5" />
                                    </div>
                                </div>
                            </div>

                            <!-- Installments Section (Rata) -->
                            <div class="form-section installments-section">
                                <h3 class="section-header">Rata</h3>

                                <!-- Monthly Rental Price -->
                                <!-- <div class="monthly-rental-price">
                                    <div class="rental-price-item">
                                        <label class="rental-price-label">MESEČNA CENA NAJMA u EUR bez PDV-a</label>
                                        <div class="rental-price-value">
                                            {{ formatNumberWithEur(currentCalc.monthly_price_without_vat) }}
                                        </div>
                                    </div>
                                </div> -->

                                <!-- Installments Summary -->
                                <!-- <div class="installments-summary">
                                    <span class="summary-text">
                                        <strong>Vrednost rata:</strong> <span class="value">{{
                                            formatNumber(currentCalc.installment_value) }}</span> |
                                        <strong>Razlika:</strong> <span class="value">{{
                                            formatNumber(currentCalc.installment_difference) }}</span>
                                    </span>
                                </div> -->

                                <!-- Installments Grid -->
                                <div class="installments-grid">
                                    <!-- BRUTO Section -->
                                    <div class="installment-group">
                                        <h4 class="installment-group-title"
                                            style="display: flex; align-items: center; gap: 8px;">
                                            BRUTO
                                            <div style="display: flex; align-items: center;">
                                                <input type="number" step="0.01" class="form-control"
                                                    style="width: 80px; height: 30px; padding: 0.25rem 0.5rem; font-size: 0.875rem;"
                                                    v-model.number="currentCalc.first_installment_percentage"
                                                    @input="calculateFirstInstallmentFromPercentageBruto" />
                                                <span style="margin-left: 4px;">%</span>
                                            </div>
                                        </h4>
                                        <div class="installment-items">
                                            <div class="installment-item">
                                                <label class="installment-label">1. rata</label>
                                                <div class="installment-value-display">
                                                    <input type="number" step="0.01" class="form-control"
                                                        v-model.number="currentCalc.first_installment_bruto"
                                                        @input="calculatePercentageFromFirstInstallmentBruto" />
                                                </div>
                                            </div>
                                            <div class="installment-item">
                                                <label class="installment-label">{{ form.contract_duration - 1 }}
                                                    rata</label>
                                                <div class="installment-value-display">
                                                    <input type="text" class="form-control"
                                                        :value="formatNumberWithEur(currentCalc.last_installment_bruto)"
                                                        readonly />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- NETO Section -->
                                    <div class="installment-group">
                                        <h4 class="installment-group-title">NETO</h4>
                                        <div class="installment-items">
                                            <div class="installment-item">
                                                <label class="installment-label">1. rata</label>
                                                <div class="installment-value-display">
                                                    <input type="text" class="form-control"
                                                        :value="formatNumberWithEur(currentCalc.first_installment_neto)"
                                                        readonly />
                                                </div>
                                            </div>
                                            <div class="installment-item">
                                                <label class="installment-label">{{ form.contract_duration - 1 }}
                                                    rata</label>
                                                <div class="installment-value-display">
                                                    <input type="text" class="form-control"
                                                        :value="formatNumberWithEur(currentCalc.last_installment_neto)"
                                                        readonly />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ReservationHeader from "../reservation/header/ReservationHeader.vue";
import LtrCalcService from "../../services/ltr-calculation.service";
import RegistrationService from "../../services/registration.service";
import KaskoService from "../../services/kasko.service";
import AoAnService from "../../services/ao-an.service";
import LtrCalculationDefaultsService from "../../services/ltr-calculation-defaults.service";
import utils from "../../utils/common";

export default {
    name: "ltr-calculation-form-basic",
    components: { ReservationHeader },
    props: { id: { type: [Number, String], required: false } },
    data() {
        return {
            isInitializingData: false,
            form: {
                id: null,
                date: new Date().toISOString().substring(0, 10),
                title: "",
                status: "Draft",
                vehicle_type: "Obicno",
                seats: "1+4",
                customer: "",
                manufacturer: "",
                vehicle: "",
                vehicle_model: "",
                is_used_vehicle: false,
                vehicle_type_selection: null,
                new_vehicle_value_gross: null,
                annual_kasko_net: null,
                registration_number: null,
                comment: null,
                ccm: null,
                kw: null,
                vehicle_value_list_pdv_discounted: null,
                vehicle_value_net: null,
                vehicle_value_vat: null,
                contract_duration: 48,
                vehicle_value_remaining: 30,
                vehicle_value_remaining_amount: null,
                vehicle_value_increased: null,
                increased_buyout_percentage: 5,
                annual_mileage: null,
                total_mileage: null,
                // Calculations array - stores all costs and totals
                calculations: [],
            },
            // Reference data
            registrationData: [],
            kaskoData: [],
            aoAnData: [],
            ccmOptions: [],
            kwOptions: [],
            // Active calculation tab index
            activeCalc: 0,
            // Default values from database
            defaults: {},
        };
    },
    computed: {
        currentCalc() {
            // Return active calculation
            if (this.form.calculations && this.form.calculations.length > 0 && this.activeCalc >= 0) {
                return this.form.calculations[this.activeCalc];
            }
            return null;
        },
        formTitle() {
            return 'LTR kalkulacija - Basic';
        },
        vehicleTypeLabel() {
            const labels = {
                new: 'Novo vozilo',
                used_primero: 'Polovno vozilo iz flote Primero',
                used_other: 'Polovno vozilo — nije vlasništvo Primero',
            };
            return labels[this.form.vehicle_type_selection] || '';
        },
    },
    methods: {
        onFieldChange() {
            this.calculateValues();
        },

        async onVehicleTypeChange() {
            await this.loadCcmOptions();

            if (this.form.vehicle_type === 'Elektricno') {
                this.form.ccm = 'Elektricno';
                this.form.kw = 'EV';
            } else {
                this.form.ccm = null;
                this.form.kw = null;
            }

            this.calculateRegistration();
            this.calculateAoAn();
        },

        onSeatsChange() {
            this.loadKwOptions();
            this.calculateAoAn();
        },

        onCcmChange() {
            this.calculateRegistration();
        },

        onKwChange() {
            this.calculateAoAn();
        },

        onContractDurationChange() {
            this.calculateKasko();
            this.calculateRegistration();
            this.calculateAoAn();
            this.calculateProracun();
            this.calculateTotals();
        },

        async calculateValues(isInitialization = false) {
            // Calculate net value and VAT
            if (this.form.vehicle_value_list_pdv_discounted) {
                this.form.vehicle_value_net = Math.round((this.form.vehicle_value_list_pdv_discounted / 1.2) * 100) / 100;
                this.form.vehicle_value_vat = Math.round((this.form.vehicle_value_list_pdv_discounted - this.form.vehicle_value_net) * 100) / 100;
            }

            // Calculate residual value
            if (!isInitialization) {
                this.calculateResidualValue();
            }

            // Calculate increased buyout
            this.calculateIncreasedBuyout();

            // Calculate proracun
            this.calculateProracun();

            // Recalculate kasko based on net value
            await this.calculateKasko();

            // Check if mechanical protection is required
            this.checkMechanicalProtection();

            // Recalculate totals
            this.calculateTotals();
        },

        calculateProracun() {
            if (!this.currentCalc) return;

            // Calculate participation amount from form.vehicle_value_net
            if (this.currentCalc.participation_percentage && this.form.vehicle_value_net) {
                this.currentCalc.participation_amount = Math.round((this.form.vehicle_value_net * this.currentCalc.participation_percentage / 100) * 100) / 100;
            } else {
                this.currentCalc.participation_amount = 0;
            }

            // Calculate financing amount (net value - participation) using form.vehicle_value_net
            if (this.form.vehicle_value_net && this.currentCalc.participation_amount !== null) {
                this.currentCalc.financing_amount = Math.round((this.form.vehicle_value_net - this.currentCalc.participation_amount) * 100) / 100;
            }

            // Calculate processing fee amount from vehicle value with VAT
            if (this.currentCalc.processing_fee_percentage && this.form.vehicle_value_list_pdv_discounted) {
                this.currentCalc.processing_fee_amount = Math.round((this.form.vehicle_value_list_pdv_discounted * this.currentCalc.processing_fee_percentage / 100) * 100) / 100;
            } else {
                this.currentCalc.processing_fee_amount = 0;
            }

            // Calculate monthly installment net using PMT formula
            // PMT(rate, nper, pv, fv, type) where:
            // rate = vat_financing_rate / 12
            // nper = contract_duration
            // pv = -financing_amount (negative because it's money borrowed)
            // fv = vehicle_value_net * vehicle_value_remaining / 100 (residual value based on net value)
            // type = 0 (payment at end of period)
            if (this.currentCalc.financing_amount && this.form.contract_duration && this.currentCalc.vat_financing_rate && this.form.vehicle_value_net) {
                const rate = this.currentCalc.vat_financing_rate / 100 / 12;  // Monthly interest rate
                const nper = this.form.contract_duration;  // Number of periods
                const pv = -this.currentCalc.financing_amount;  // Present value (negative)
                const fv = this.form.vehicle_value_net * (this.form.vehicle_value_remaining || 0) / 100;  // Future value (residual)

                if (rate > 0) {
                    // PMT formula: rate * (pv * (1 + rate)^nper + fv) / ((1 + rate)^nper - 1)
                    const pvFactor = pv * Math.pow(1 + rate, nper);
                    const pmt = rate * (pvFactor + fv) / (Math.pow(1 + rate, nper) - 1);
                    this.currentCalc.monthly_installment_net = Math.round(-pmt * 100) / 100;  // Negative to make result positive
                } else {
                    // No interest - simple division
                    this.currentCalc.monthly_installment_net = Math.round(((this.currentCalc.financing_amount - fv) / nper) * 100) / 100;
                }
            } else {
                this.currentCalc.monthly_installment_net = 0;
            }

            // Calculate financed PDV monthly using form.vehicle_value_vat and form.contract_duration
            if (this.form.vehicle_value_vat && this.form.contract_duration && this.currentCalc.vat_financing_rate) {
                const monthlyRate = this.currentCalc.vat_financing_rate / 100 / 12;

                if (monthlyRate > 0) {
                    const pmt = this.form.vehicle_value_vat * (monthlyRate * Math.pow(1 + monthlyRate, this.form.contract_duration)) /
                        (Math.pow(1 + monthlyRate, this.form.contract_duration) - 1);
                    this.currentCalc.financed_pdv_monthly = Math.round(pmt * 100) / 100;
                } else {
                    this.currentCalc.financed_pdv_monthly = Math.round((this.form.vehicle_value_vat / this.form.contract_duration) * 100) / 100;
                }
            } else {
                this.currentCalc.financed_pdv_monthly = 0;
            }

            // After financing-related values change, update totals dependent on them
            this.calculateTotals();
        },

        calculateResidualValue() {
            if (this.form.vehicle_value_list_pdv_discounted && this.form.vehicle_value_remaining !== null && this.form.vehicle_value_remaining !== undefined) {
                this.form.vehicle_value_remaining_amount = Math.round((this.form.vehicle_value_list_pdv_discounted * this.form.vehicle_value_remaining / 100) * 100) / 100;
            } else if (this.form.vehicle_value_list_pdv_discounted && !this.form.vehicle_value_remaining) {
                // If percentage is 0 or empty, remaining amount is 0
                this.form.vehicle_value_remaining_amount = 0;
            }
            this.calculateIncreasedBuyout();
            this.calculateProracun();
        },

        calculateResidualPercentage() {
            if (this.form.vehicle_value_list_pdv_discounted && this.form.vehicle_value_remaining_amount !== null && this.form.vehicle_value_remaining_amount !== undefined) {
                this.form.vehicle_value_remaining = Math.round((this.form.vehicle_value_remaining_amount / this.form.vehicle_value_list_pdv_discounted * 100) * 100) / 100;
            } else if (this.form.vehicle_value_list_pdv_discounted && !this.form.vehicle_value_remaining_amount) {
                // If amount is 0 or empty, percentage is 0
                this.form.vehicle_value_remaining = 0;
            }
            this.calculateIncreasedBuyout();
            this.calculateProracun();
        },

        calculateIncreasedBuyout() {
            if (this.form.vehicle_value_remaining_amount !== null && this.form.vehicle_value_remaining_amount !== undefined) {
                if (this.form.increased_buyout_percentage) {
                    this.form.vehicle_value_increased = Math.round((this.form.vehicle_value_remaining_amount * (1 + this.form.increased_buyout_percentage / 100)) * 100) / 100;
                } else {
                    // If no increase percentage, increased value equals remaining amount
                    this.form.vehicle_value_increased = this.form.vehicle_value_remaining_amount;
                }
            }
        },

        async loadCcmOptions() {
            if (!this.form.vehicle_type) return;

            try {
                const response = await RegistrationService.getAllActive();
                const data = response.data;

                // Filter by vehicle type and get unique CCM values
                const ccmSet = new Set();
                data.forEach(item => {
                    if (item.type === this.form.vehicle_type) {
                        ccmSet.add(item.zapremina_ccm);
                    }
                });

                // Convert Set to sorted array
                this.ccmOptions = Array.from(ccmSet).sort();
            } catch (error) {
                console.error('Error loading CCM options:', error);
                this.$store.commit("app/showAlert", {
                    type: "warning",
                    message: "Greška pri učitavanju CCM opcija"
                });
            }
        },

        async loadKwOptions() {
            if (!this.form.seats) return;

            try {
                const response = await AoAnService.getAllActive();
                const data = response.data;

                // Filter by seats type and get unique KW values
                const kwSet = new Set();
                data.forEach(item => {
                    if (item.type === this.form.seats) {
                        kwSet.add(item.snaga_kw);
                    }
                });

                // Convert Set to sorted array
                this.kwOptions = Array.from(kwSet).sort();
            } catch (error) {
                console.error('Error loading KW options:', error);
                this.$store.commit("app/showAlert", {
                    type: "warning",
                    message: "Greška pri učitavanju KW opcija"
                });
            }
        },

        async calculateRegistration() {
            if (!this.form.vehicle_type || !this.form.contract_duration || !this.currentCalc) return;

            // For Elektricno type, CCM is not required
            if (this.form.vehicle_type !== 'Elektricno' && !this.form.ccm) return;

            try {
                // Get active registration data
                const response = await RegistrationService.getAllActive();
                const data = response.data;

                let match;

                if (this.form.vehicle_type === 'Elektricno') {
                    // For electric vehicles, find the record with zapremina_ccm = 'Elektricno'
                    match = data.find(item => {
                        return item.type === 'Elektricno' && item.zapremina_ccm === 'Elektricno';
                    });
                } else {
                    // For other types, find matching record based on vehicle type and exact CCM range match
                    match = data.find(item => {
                        return item.type === this.form.vehicle_type && item.zapremina_ccm === this.form.ccm;
                    });
                }

                if (match) {
                    // Calculate number of years from contract duration (months)
                    const years = Math.ceil(this.form.contract_duration / 12);

                    // Determine which year column to use (godina_1 to godina_5, max 5 years)
                    const yearColumn = `godina_${Math.min(years, 5)}`;

                    // Use the appropriate year column value
                    this.currentCalc.registration_total = parseFloat(match[yearColumn]) || 0;
                } else {
                    console.warn('No matching registration found for vehicle type and ccm');
                    this.currentCalc.registration_total = 0;
                }

                this.calculateTotals();
            } catch (error) {
                console.error('Error calculating registration:', error);
                this.$store.commit("app/showAlert", {
                    type: "warning",
                    message: "Greška pri učitavanju cene registracije"
                });
            }
        },

        async calculateKasko() {
            // For type 3 (used_other), kasko is entered manually - skip auto-calculation
            if (this.form.vehicle_type_selection === 'used_other') {
                this.calculateTotals();
                return;
            }

            // Determine base value for Kasko
            let kaskoBaseValue = this.form.vehicle_value_list_pdv_discounted;
            if (this.form.vehicle_type_selection === 'used_primero' && this.form.new_vehicle_value_gross) {
                kaskoBaseValue = this.form.new_vehicle_value_gross;
            }

            if (!kaskoBaseValue || !this.form.contract_duration || !this.currentCalc) return;

            // console.log('Calculating kasko for value:', kaskoBaseValue, 'and contract duration:', this.form.contract_duration);
            try {
                // Get active kasko data
                const response = await KaskoService.getAllActive();
                const data = response.data;

                // Find matching record based on value with VAT range
                const match = data.find(item => {
                    if (!item.cena_range) return false;

                    const range = item.cena_range.toLowerCase();
                    if (range.includes('do')) {
                        const max = parseInt(range.match(/\d+/)[0]);
                        return kaskoBaseValue <= max;
                    } else if (range.includes('preko')) {
                        const min = parseInt(range.match(/\d+/)[0]);
                        return kaskoBaseValue > min;
                    }
                    return false;
                });

                if (match) {
                    // Determine which percentage to use based on contract duration
                    let percentage = 0;
                    if (this.form.contract_duration === 12) {
                        percentage = parseFloat(match.najam_12_meseci) || 0;
                    } else if (this.form.contract_duration >= 12 && this.form.contract_duration <= 36) {
                        percentage = parseFloat(match.meseci_12_36) || 0;
                    } else {
                        percentage = parseFloat(match.meseci_48_plus) || 0;
                    }

                    // Calculate number of years from contract duration (months)
                    const years = Math.ceil(this.form.contract_duration / 12);

                    // console.log('Kasko match found:', match, 'using percentage:', percentage, 'for years:', years);

                    // Calculate kasko cost: (base value * percentage / 100) * number of years
                    // Note: Original formula divided by 1.2 (neto base?), but here we use the gross value as base for range lookup.
                    // The formula (value * percentage / 100) / 1.2 suggests deriving net annual cost?
                    // Assuming existing logic is correct: (Gross * % / 100) / 1.2
                    const annualKasko = (kaskoBaseValue * percentage / 100) / 1.2;
                    this.currentCalc.auto_kasko_total = Math.round((annualKasko * years) * 100) / 100;
                } else {
                    console.warn('No matching kasko found for value');
                    this.currentCalc.auto_kasko_total = 0;
                }

                this.calculateTotals();
            } catch (error) {
                console.error('Error calculating kasko:', error);
                this.$store.commit("app/showAlert", {
                    type: "warning",
                    message: "Greška pri učitavanju cene kasko osiguranja"
                });
            }
        },

        async calculateAoAn() {
            if (!this.form.seats || !this.form.kw || !this.form.contract_duration || !this.currentCalc) return;

            try {
                // Get active AO/AN data
                const response = await AoAnService.getAllActive();
                const data = response.data;

                let match;

                if (this.form.vehicle_type === 'Elektricno') {
                    // For electric vehicles, always take the lowest amount (lowest KW range)
                    const seatRecords = data.filter(item => item.type === this.form.seats);

                    // Helper to extract numeric value for sorting
                    const getSortValue = (kwRange) => {
                        if (!kwRange) return 999;
                        const lower = kwRange.toLowerCase();
                        if (lower.startsWith('do')) return 0;
                        const match = lower.match(/\d+/);
                        return match ? parseInt(match[0]) : 999;
                    };

                    seatRecords.sort((a, b) => getSortValue(a.snaga_kw) - getSortValue(b.snaga_kw));
                    match = seatRecords[0];
                } else {
                    // Find matching record based on seats type and exact KW range match
                    match = data.find(item => {
                        return item.type === this.form.seats && item.snaga_kw === this.form.kw;
                    });
                }

                if (match) {
                    // Calculate number of years from contract duration (months)
                    const years = Math.ceil(this.form.contract_duration / 12);

                    // Determine which year column to use (godina_1 to godina_5, max 5 years)
                    const yearColumn = `godina_${Math.min(years, 5)}`;

                    // Use the appropriate year column value
                    this.currentCalc.ao_personal_insurance_total = parseFloat(match[yearColumn]) || 0;
                } else {
                    console.warn('No matching AO/AN found for seats and kw');
                    this.currentCalc.ao_personal_insurance_total = 0;
                }

                this.calculateTotals();
            } catch (error) {
                console.error('Error calculating AO/AN:', error);
                this.$store.commit("app/showAlert", {
                    type: "warning",
                    message: "Greška pri učitavanju cene AO/AN osiguranja"
                });
            }
        },

        checkMechanicalProtection() {
            if (!this.currentCalc) return;
            // Auto-fill privremeno disablovano
            this.calculateTotals();
        },

        calculateTotals() {
            if (!this.currentCalc) return;

            // Sum all costs
            const contractDuration = this.form.contract_duration || 0;
            const financingTotal = (this.currentCalc.monthly_installment_net || 0) * contractDuration; // Mesečna rata Neto * Period

            // Use saved percentage if it exists, otherwise use current default
            // This ensures saved calculations preserve their original percentage
            const manipulativniTroskoviProcenat = this.currentCalc.handling_fee_percentage !== undefined && this.currentCalc.handling_fee_percentage !== null
                ? parseFloat(this.currentCalc.handling_fee_percentage)
                : parseFloat(this.defaults.manipulativni_troskovi_procenat || 0.15);

            const handlingFeeBasic = (this.form.vehicle_value_net || 0) * (manipulativniTroskoviProcenat / 100); // Neto vrednost vozila * manipulativni_troskovi_procenat%

            // Persist helpful breakdown values (optional, also useful for saving/debug/UI if needed)
            this.currentCalc.financing_total = Math.round(financingTotal * 100) / 100;
            this.currentCalc.handling_fee_basic = Math.round(handlingFeeBasic * 100) / 100;

            // For type 3, kasko is entered as annual net value - convert to total for contract duration
            const kaskoContribution = this.form.vehicle_type_selection === 'used_other'
                ? ((this.form.annual_kasko_net || 0) / 12) * contractDuration
                : (this.currentCalc.auto_kasko_total || 0);

            const costs = [
                kaskoContribution,
                this.currentCalc.registration_total || 0,
                this.currentCalc.mandatory_equipment_total || 0,
                this.currentCalc.ao_personal_insurance_total || 0,
                this.currentCalc.mechanical_protection_total || 0,
                this.currentCalc.financing_total || 0,
                this.currentCalc.handling_fee_basic || 0,
            ];

            this.currentCalc.total_costs = Math.round(costs.reduce((sum, cost) => sum + cost, 0) * 100) / 100;

            // Calculate monthly costs
            if (this.form.contract_duration) {
                this.currentCalc.monthly_costs = Math.round((this.currentCalc.total_costs / this.form.contract_duration) * 100) / 100;
            }

            // Calculate margin
            if (this.currentCalc.margin_percentage !== null && this.currentCalc.margin_percentage !== undefined && this.currentCalc.margin_percentage !== '') {
                this.currentCalc.margin_amount = Math.round((this.currentCalc.total_costs * this.currentCalc.margin_percentage / 100) * 100) / 100;
            } else {
                this.currentCalc.margin_amount = 0;
            }

            // Calculate total with margin
            const totalWithMargin = this.currentCalc.total_costs + (this.currentCalc.margin_amount || 0);

            // Calculate Europcar fee
            if (this.currentCalc.europcar_fee_percentage !== null && this.currentCalc.europcar_fee_percentage !== undefined && this.currentCalc.europcar_fee_percentage !== '') {
                this.currentCalc.europcar_fee_amount = Math.round((totalWithMargin * this.currentCalc.europcar_fee_percentage / 100) * 100) / 100;
            } else {
                this.currentCalc.europcar_fee_amount = 0;
            }

            // Calculate total price without VAT
            this.currentCalc.total_price_without_vat = Math.round((totalWithMargin + (this.currentCalc.europcar_fee_amount || 0)) * 100) / 100;

            // Calculate monthly price without VAT
            if (this.form.contract_duration) {
                this.currentCalc.monthly_price_without_vat = Math.round((this.currentCalc.total_price_without_vat / this.form.contract_duration) * 100) / 100;
            }

            // Calculate installments (rata)
            this.calculateInstallments();
        },

        calculateInstallments() {
            if (!this.currentCalc || !this.form.contract_duration) return;

            const ukupnaCenaBezPdvMesecno = this.currentCalc.monthly_price_without_vat || 0;
            const contractDuration = this.form.contract_duration;
            const ukupnaCenaBezPdv = ukupnaCenaBezPdvMesecno * contractDuration;
            const ukupnaCenaSaPdv = ukupnaCenaBezPdv * 1.2;

            // Calculate installment_value and installment_difference
            this.currentCalc.installment_value = Math.round(ukupnaCenaBezPdv * 1.2 * 100) / 100;
            this.currentCalc.installment_difference = Math.round((this.currentCalc.installment_value + (this.form.vehicle_value_remaining_amount || 0) - (this.form.vehicle_value_list_pdv_discounted || 0)) * 100) / 100;

            if (this.currentCalc.first_installment_percentage === undefined || this.currentCalc.first_installment_percentage === null) {
                this.currentCalc.first_installment_percentage = 20;
            }

            if (!this.isInitializingData) {
                // Ako imamo manuelno unet bruto iznos (veći od 0), fiksiramo njega i ažuriramo procenat
                // U suprotnom, računamo bruto iznos na osnovu procenta.
                if (this.currentCalc.first_installment_bruto > 0) {
                    if (ukupnaCenaSaPdv > 0) {
                        this.currentCalc.first_installment_percentage = Math.round((this.currentCalc.first_installment_bruto / ukupnaCenaSaPdv) * 100 * 100) / 100;
                    } else {
                        this.currentCalc.first_installment_percentage = 0;
                    }
                    this.currentCalc.first_installment_neto = Math.round((this.currentCalc.first_installment_bruto / 1.2) * 100) / 100;
                } else {
                    const percentage = parseFloat(this.currentCalc.first_installment_percentage) || 0;
                    this.currentCalc.first_installment_bruto = Math.round(ukupnaCenaSaPdv * (percentage / 100) * 100) / 100;
                    this.currentCalc.first_installment_neto = Math.round((this.currentCalc.first_installment_bruto / 1.2) * 100) / 100;
                }
            } else {
                if (this.currentCalc.first_installment_neto && !this.currentCalc.first_installment_bruto) {
                    this.currentCalc.first_installment_bruto = Math.round(this.currentCalc.first_installment_neto * 1.2 * 100) / 100;
                }
            }

            this.updateRelatedInstallments();
        },

        calculateFirstInstallmentFromPercentageBruto() {
            if (!this.currentCalc || !this.form.contract_duration) return;
            const ukupnaCenaBezPdvMesecno = this.currentCalc.monthly_price_without_vat || 0;
            const contractDuration = this.form.contract_duration;
            const ukupnaCenaSaPdv = (ukupnaCenaBezPdvMesecno * contractDuration) * 1.2;

            const percentage = parseFloat(this.currentCalc.first_installment_percentage) || 0;
            this.currentCalc.first_installment_bruto = Math.round(ukupnaCenaSaPdv * (percentage / 100) * 100) / 100;
            this.currentCalc.first_installment_neto = Math.round((this.currentCalc.first_installment_bruto / 1.2) * 100) / 100;
        },

        calculatePercentageFromFirstInstallmentBruto() {
            if (!this.currentCalc || !this.form.contract_duration) return;
            const ukupnaCenaBezPdvMesecno = this.currentCalc.monthly_price_without_vat || 0;
            const contractDuration = this.form.contract_duration;
            const ukupnaCenaSaPdv = (ukupnaCenaBezPdvMesecno * contractDuration) * 1.2;

            if (ukupnaCenaSaPdv > 0) {
                this.currentCalc.first_installment_percentage = Math.round(((this.currentCalc.first_installment_bruto || 0) / ukupnaCenaSaPdv) * 100 * 100) / 100;
            } else {
                this.currentCalc.first_installment_percentage = 0;
            }

            this.currentCalc.first_installment_neto = Math.round(((this.currentCalc.first_installment_bruto || 0) / 1.2) * 100) / 100;
        },

        updateRelatedInstallments() {
            if (!this.currentCalc || !this.form.contract_duration) return;
            const ukupnaCenaBezPdvMesecno = this.currentCalc.monthly_price_without_vat || 0;
            const contractDuration = this.form.contract_duration;
            const ukupnaCenaBezPdv = ukupnaCenaBezPdvMesecno * contractDuration;

            // Uvek racunamo ostale rate na osnovu pretpostavljene prve rate od 20%
            // kako se ostale rate ne bi menjale kada korisnik menja prvu ratu
            const baseFirstInstallmentNeto = Math.round(ukupnaCenaBezPdv * 0.20 * 100) / 100;

            if (contractDuration > 1) {
                this.currentCalc.last_installment_neto = Math.round(((ukupnaCenaBezPdv - baseFirstInstallmentNeto) / (contractDuration - 1)) * 100) / 100;
            } else {
                this.currentCalc.last_installment_neto = 0;
            }

            this.currentCalc.last_installment_bruto = Math.round(this.currentCalc.last_installment_neto * 1.2 * 100) / 100;
        },

        formatCurrency(value) {
            if (!value && value !== 0) return '0.00 EUR';
            return `${parseFloat(value).toFixed(2)} EUR`;
        },

        formatNumber(value) {
            if (!value && value !== 0) return '0';
            return parseFloat(value).toLocaleString('en');
        },

        formatNumberWithEur(value) {
            if (!value && value !== 0) return '0 €';
            return parseFloat(value).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
        },

        addCalculation() {
            // Create new calculation with default values
            const newCalculation = {
                calc_title: `Kalkulacija ${this.form.calculations.length + 1}`,
                ...this.getDefaultCalculation()
            };

            this.form.calculations.push(newCalculation);
            this.activeCalc = this.form.calculations.length - 1;

            // Recalculate for new tab
            this.calculateProracun();
            this.calculateKasko();
            this.calculateRegistration();
            this.calculateAoAn();
        },

        removeCalculation(idx) {
            if (idx < 0 || idx >= this.form.calculations.length) return;

            // Don't allow removing if it's the last calculation
            if (this.form.calculations.length === 1) {
                this.$store.commit("app/showAlert", {
                    type: "warning",
                    message: "Ne možete obrisati poslednju kalkulaciju."
                });
                return;
            }

            this.form.calculations.splice(idx, 1);

            // Adjust active calc if needed
            if (this.activeCalc >= this.form.calculations.length) {
                this.activeCalc = this.form.calculations.length - 1;
            }
        },

        getDefaultCalculation() {
            return {
                // Proracun fields
                participation_percentage: 0,
                participation_amount: null,
                financing_amount: null,
                reprogramming_note: '',
                interest_rate: parseFloat(this.defaults.kamatna_stopa_procenat || 0),
                vat_financing_rate: parseFloat(this.defaults.kamatna_stopa_finan_pdv_procenat || 0),
                processing_fee_percentage: parseFloat(this.defaults.troskovi_obrade_sa_pdv_procenat || 0),
                processing_fee_amount: null,
                monthly_installment_net: null,
                financed_pdv_monthly: null,
                // Manipulative costs
                auto_kasko_total: null,
                registration_total: null,
                mandatory_equipment_total: 100,
                ao_personal_insurance_total: null,
                mechanical_protection_total: null,
                handling_fee_percentage: parseFloat(this.defaults.manipulativni_troskovi_procenat || 0.15),
                margin_percentage: 9,
                europcar_fee_percentage: parseFloat(this.defaults.europcar_fee_procenat || 1.5),
                first_installment_percentage: 20,
                // Totals
                total_costs: 0,
                monthly_costs: 0,
                margin_amount: 0,
                europcar_fee_amount: 0,
                total_price_without_vat: 0,
                monthly_price_without_vat: 0,
            };
        },

        async loadData() {
            const pid = this.id ?? this.$route?.params?.id;
            if (!pid || pid === 'new') {
                // For new forms, read vehicle type from route query
                if (this.$route?.query?.vehicleType) {
                    this.form.vehicle_type_selection = this.$route.query.vehicleType;
                }
                // Initialize with empty calculation for new forms - apply defaults
                this.form.calculations = [this.getDefaultCalculation()];
                // Calculate initial values if any defaults are set
                this.calculateResidualValue();
                this.calculateIncreasedBuyout();
                return;
            }

            try {
                const { data } = await LtrCalcService.get(pid);

                // Strip deprecated fields if present without creating unused bindings
                const clean = { ...(data || {}) };
                delete clean.ks;
                delete clean.exchange_rate;

                // Assign loaded data (without ks/exchange_rate)
                this.form = { ...this.form, ...clean };

                // Backward compatibility: derive vehicle_type_selection from is_used_vehicle for old records
                if (!this.form.vehicle_type_selection) {
                    this.form.vehicle_type_selection = this.form.is_used_vehicle ? 'used_primero' : 'new';
                }

                // Ensure correct defaults for Electric vehicles
                if (this.form.vehicle_type === 'Elektricno') {
                    this.form.ccm = 'Elektricno';
                    this.form.kw = 'EV';
                }

                // Reload reference options for the loaded vehicle type/seats
                await this.loadCcmOptions();
                await this.loadKwOptions();

                // For existing saved data, use the saved calculations as-is
                // Do NOT apply defaults - the saved values should be preserved
                if (!this.form.calculations || this.form.calculations.length === 0) {
                    // Only if there's truly no data, create a minimal structure
                    this.form.calculations = [{
                        participation_percentage: 0,
                        participation_amount: null,
                        financing_amount: null,
                        reprogramming_note: '',
                        interest_rate: 0,
                        vat_financing_rate: 0,
                        processing_fee_percentage: 0,
                        processing_fee_amount: null,
                        monthly_installment_net: null,
                        financed_pdv_monthly: null,
                        auto_kasko_total: null,
                        registration_total: null,
                        mandatory_equipment_total: 100,
                        ao_personal_insurance_total: null,
                        mechanical_protection_total: null,
                        handling_fee_percentage: 0.15, // Default fallback for old data
                        margin_percentage: 9,
                        europcar_fee_percentage: 1.5,
                        total_costs: 0,
                        monthly_costs: 0,
                        margin_amount: 0,
                        europcar_fee_amount: 0,
                        total_price_without_vat: 0,
                        monthly_price_without_vat: 0,
                    }];
                } else {
                    // Ensure all calculations have handling_fee_percentage
                    // This is for backward compatibility with old saved data
                    this.form.calculations = this.form.calculations.map(calc => {
                        if (calc.handling_fee_percentage === undefined || calc.handling_fee_percentage === null) {
                            calc.handling_fee_percentage = parseFloat(this.defaults.manipulativni_troskovi_procenat || 0.15);
                        }
                        if (calc.first_installment_percentage === undefined || calc.first_installment_percentage === null) {
                            calc.first_installment_percentage = 20;
                        }
                        return calc;
                    });
                }

                // Recalculate all values based on saved data, skipping pure recalculations that drift due to rounding
                this.isInitializingData = true;
                await this.calculateValues(true);
                this.isInitializingData = false;
            } catch (error) {
                console.error('Error loading LTR calculation:', error);
                this.$store.commit("app/showAlert", {
                    type: "danger",
                    message: "Greška pri učitavanju kalkulacije: " + (error.response?.data?.message || error.message)
                });
            }
        },

        async onSave() {
            try {
                // Ensure title and status have defaults if not set
                const saveData = {
                    ...this.form,
                    title: this.form.title || `LTR Basic ${this.form.customer || ''} ${this.form.date || ''}`.trim(),
                    status: this.form.status || 'Draft',
                };

                if (this.form.id) {
                    await LtrCalcService.update(this.form.id, saveData);
                } else {
                    const { data } = await LtrCalcService.create(saveData);
                    this.form.id = data.id;
                }
                this.$store.commit("app/showAlert", { type: "success", message: "Sačuvano." });
                if (this.$route && this.$route.name && this.$route.name.startsWith('ltr')) {
                    this.$router.push({ name: 'ltr' });
                } else {
                    this.$emit('saved', this.form);
                }
            } catch (e) {
                this.$store.commit("app/showAlert", { type: "danger", message: e.response?.data?.message || e.message });
            }
        },

        onDelete() {
            if (!this.form.id) return;
            utils.showDialog(
                this.form,
                "Da li ste sigurni da želite da obrišete LTR kalkulaciju?",
                `Brisanje LTR kalkulacije: ${this.form.id}`,
                async () => {
                    try {
                        await LtrCalcService.delete(this.form.id);
                        this.$store.commit("app/showAlert", { type: "success", message: "Obrisano." });
                        if (this.$route && this.$route.name && this.$route.name.startsWith('ltr')) {
                            this.$router.go(-1);
                        } else {
                            this.$emit('deleted', this.form.id);
                        }
                    } catch (e) {
                        this.$store.commit("app/showAlert", { type: "danger", message: e.response?.data?.message || e.message });
                    }
                }
            );
        },
    },
    async mounted() {
        // Load defaults first
        try {
            const { data } = await LtrCalculationDefaultsService.getAll();
            // Convert array to object with key-value pairs
            this.defaults = data.reduce((acc, item) => {
                acc[item.key] = item.value;
                return acc;
            }, {});
        } catch (error) {
            console.error('Error loading defaults:', error);
            // Continue with hardcoded defaults if API fails
        }

        // Load reference data
        await Promise.all([
            RegistrationService.getAllActive().then(res => { this.registrationData = res.data; }),
            KaskoService.getAllActive().then(res => { this.kaskoData = res.data; }),
            AoAnService.getAllActive().then(res => { this.aoAnData = res.data; })
        ]);

        // Load CCM options based on default vehicle type
        await this.loadCcmOptions();
        // Load KW options based on default seats
        await this.loadKwOptions();

        // Load data if editing existing or initialize new
        await this.loadData();
    },
};
</script>

<style scoped>
.page {
    max-width: 1200px;
    margin-left: 30px;
    display: flow-root;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.ltr-form-container {
    background-color: #ffffff;
    border-radius: 0.75rem;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.form-header {
    padding: 2rem;
}

.basic-info-section,
.costs-section,
.summary-section {
    padding: 2rem;
    border-bottom: 1px solid #e5e7eb;
}

.summary-section {
    border-bottom: none;
    background-color: #f9fafb;
}

.section-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 1.5rem;
    padding-bottom: 0.75rem;
    border-bottom: 2px solid #3b82f6;
}

.section-title-type {
    font-size: 1rem;
    font-weight: 400;
    color: #6b7280;
}

/* Tab styling */
.nav-tabs .nav-link {
    cursor: pointer;
}

.nav-tabs .nav-link.active {
    background-color: #fff;
    border-bottom-color: #fff;
}

.tab-content {
    background: #fff;
    border-radius: 0 0 0.375rem 0.375rem;
}

.tab-pane {
    animation: fadeIn 0.3s;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

.form-section {
    border: 1px solid #e5e7eb;
    border-radius: 0.75rem;
    padding: 1.5rem;
    background-color: #fafafa;
    margin-bottom: 1.5rem;
}

.total-section {
    background-color: #f0fdf4;
    border-color: #bbf7d0;
}

.total-section .section-header {
    color: #166534;
    border-color: #dcfce7;
}

.proracun-summary {
    background-color: #ffffff;
    border: 1px solid #dcfce7;
    border-radius: 0.5rem;
    padding: 1rem 1.5rem;
    margin-bottom: 1.5rem;
}

.summary-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1rem 2rem;
    align-items: center;
}

.summary-row:not(:last-child) {
    margin-bottom: 0.75rem;
}

.summary-item-inline {
    display: flex;
    gap: 0.5rem;
    align-items: center;
}

.summary-info-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #166534;
    white-space: nowrap;
}

.summary-info-value {
    font-size: 0.875rem;
    font-weight: 700;
    color: #111827;
    white-space: nowrap;
}

.section-header {
    font-size: 1.125rem;
    font-weight: 600;
    color: #1f2937;
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 0.75rem;
    margin-bottom: 1.5rem;
}

.totals-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1.5rem;
}

.total-item {
    display: flex;
    flex-direction: column;
}

.total-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.5rem;
}

.form-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1.5rem;
}

.form-group {
    display: flex;
    flex-direction: column;
}

.form-group-full {
    grid-column: 1 / -1;
}

.form-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.5rem;
}

.form-control {
    padding: 0.5rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-control:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-control:readonly {
    background-color: #f3f4f6;
    color: #6b7280;
}

.costs-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
}

.cost-item {
    display: flex;
    flex-direction: column;
}

.cost-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.5rem;
}

.summary-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
}

.summary-item {
    padding: 1.5rem;
    background-color: #ffffff;
    border-radius: 0.5rem;
    border: 1px solid #e5e7eb;
}

.summary-item.highlight {
    background-color: #eff6ff;
    border-color: #3b82f6;
}

.summary-item.highlight-success {
    background-color: #f0fdf4;
    border-color: #22c55e;
}

.summary-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
    margin-bottom: 0.5rem;
    display: block;
}

.summary-value {
    font-size: 1.25rem;
    font-weight: 700;
    color: #111827;
}

.summary-value.large {
    font-size: 1.5rem;
    color: #22c55e;
}

.form-text {
    font-size: 0.75rem;
    margin-top: 0.25rem;
}

@media (max-width: 1200px) {
    .form-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .costs-grid,
    .summary-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .summary-row {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 768px) {

    .form-grid,
    .costs-grid,
    .summary-grid {
        grid-template-columns: 1fr;
    }

    .summary-row {
        grid-template-columns: 1fr;
    }
}

/* Monthly Rental Price Section */
.monthly-rental-price {
    background-color: #f0fdf4;
    border: 2px solid #22c55e;
    border-radius: 0.5rem;
    padding: 1.25rem 1.5rem;
    margin-bottom: 1.5rem;
}

.rental-price-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.rental-price-label {
    font-size: 1rem;
    font-weight: 700;
    color: #166534;
    text-transform: uppercase;
    letter-spacing: 0.025em;
}

.rental-price-value {
    font-size: 1.5rem;
    font-weight: 800;
    color: #166534;
}

/* Installments Section */
.installments-summary {
    background-color: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 0.5rem;
    padding: 1rem 1.5rem;
    margin-bottom: 1.5rem;
}

.summary-text {
    font-size: 0.875rem;
    color: #1e40af;
}

.summary-text strong {
    font-weight: 600;
}

.summary-text .value {
    font-weight: 700;
    color: #1e3a8a;
}

.installments-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
}

.installment-group {
    background-color: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    padding: 1.25rem;
}

.installment-group-title {
    font-size: 1rem;
    font-weight: 700;
    color: #111827;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #e5e7eb;
}

.percentage-indicator {
    font-size: 0.875rem;
    font-weight: 600;
    color: #6b7280;
    margin-left: 0.5rem;
}

.installment-items {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.installment-item {
    display: flex;
    flex-direction: column;
}

.installment-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.5rem;
}

.installment-value-display input {
    font-weight: 700;
    color: #111827;
}

@media (max-width: 768px) {
    .installments-grid {
        grid-template-columns: 1fr;
    }

    .rental-price-item {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.5rem;
    }
}
</style>
